import { createContext, useContext, useState, useCallback } from 'react';

/**
 * PlatformContext — global state for dashboard filtering and view mode.
 *
 * Shares: platform, version, build, environment, viewMode
 * Used by: GlobalHeader (sets them), UnifiedDashboard (reads them),
 *          all child components that need filter context.
 */
const PlatformContext = createContext(null);

export function PlatformProvider({ children }) {
  const [platform, setPlatformState] = useState('all');
  const [version, setVersionState] = useState(null);
  const [build, setBuildState] = useState(null);
  const [environment, setEnvironmentState] = useState('production');
  const [viewMode, setViewModeState] = useState('executive'); // 'executive' | 'developer'

  const setPlatform = useCallback((p) => {
    setPlatformState(p || 'all');
    setVersionState(null);
    setBuildState(null);
  }, []);

  const setVersion = useCallback((v) => {
    setVersionState(v || null);
    if (!v) setBuildState(null);
  }, []);

  const setBuild = useCallback((b) => {
    setBuildState(b || null);
  }, []);

  const setVersionAndBuild = useCallback((v, b) => {
    setVersionState(v || null);
    setBuildState(b || null);
  }, []);

  const setEnvironment = useCallback((env) => {
    setEnvironmentState(env || 'production');
  }, []);

  const setViewMode = useCallback((mode) => {
    setViewModeState(mode === 'developer' ? 'developer' : 'executive');
  }, []);

  return (
    <PlatformContext.Provider value={{
      platform, setPlatform,
      version, setVersion,
      build, setBuild,
      setVersionAndBuild,
      environment, setEnvironment,
      viewMode, setViewMode,
    }}>
      {children}
    </PlatformContext.Provider>
  );
}

export const usePlatform = () => {
  const ctx = useContext(PlatformContext);
  if (!ctx) return {
    platform: 'all', setPlatform: () => {},
    version: null, setVersion: () => {},
    build: null, setBuild: () => {},
    setVersionAndBuild: () => {},
    environment: 'production', setEnvironment: () => {},
    viewMode: 'executive', setViewMode: () => {},
  };
  return ctx;
};
